import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  LayoutDashboard,
  Users,
  BookOpen,
  MessageSquare,
  Building2,
  LogOut,
  Network,
  Search,
  Library,
  Cpu,
  Globe,
  ClipboardList,
  ChevronRight,
  ChevronsUpDown,
  FileText,
  UserPlus,
  HelpCircle,
  Database,
  Microscope,
  FolderOpen,
  Clock,
  Image,
  Shield,
  ShieldAlert,
  Calculator,
  Zap,
  GitCompare,
  AlertOctagon,
  Scale,
  CreditCard,
  Settings,
  Share2,
  Link2,
  BarChart3,
  Upload,
  Tag,
  CalendarDays,
  Sparkles,
  DollarSign,
  CheckCheck,
  AlertTriangle,
  Activity,
  FileWarning,
  FileCheck,
  FileSearch,
  Lock as LockIcon,
  Sparkles,
  Star,
  RefreshCw,
  Inbox,
  GraduationCap,
} from 'lucide-react';
import { cognitoAuth } from '@/services/auth/cognito';
import logoImg from '@/assets/img/logo.png';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarSeparator,
} from '@/components/ui/sidebar';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import { FEATURES } from '@/config/features';

interface NavItem {
  title: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  minPlan?: number; // 0=trial, 1=starter, 2=pro, 3=enterprise, 4=scale
}

// Super Admin only navigation - Platform management
const superAdminNavItems: NavItem[] = [
  {
    title: 'Demo requests',
    href: '/panel/admin/demo-requests',
    icon: Inbox,
  },
  {
    title: 'Workshops',
    href: '/panel/admin/workshops',
    icon: GraduationCap,
  },
  {
    title: 'Revenue',
    href: '/panel/admin/revenue',
    icon: DollarSign,
  },
  {
    title: 'Tenants',
    href: '/panel/tenants',
    icon: Building2,
  },
  {
    title: 'AI Settings',
    href: '/panel/ai-settings',
    icon: Cpu,
  },
  {
    title: 'Global Knowledge',
    href: '/panel/global-knowledge',
    icon: Library,
  },
  {
    title: 'ATO Knowledge Base',
    href: '/panel/ato-knowledge',
    icon: BookOpen,
  },
  {
    title: 'ASIC Knowledge Base',
    href: '/panel/asic-knowledge',
    icon: Shield,
  },
  {
    title: 'KB Monitor (ATO/ASIC)',
    href: '/panel/kb-monitor',
    icon: RefreshCw,
  },
  {
    title: 'ATO Upload',
    href: '/panel/ato-upload',
    icon: Upload,
  },
  {
    title: 'Document Types',
    href: '/panel/system-document-types',
    icon: Database,
  },
  {
    title: 'Knowledge Gaps',
    href: '/panel/knowledge-gaps',
    icon: AlertTriangle,
  },
];

// Tenant Admin navigation - Settings
const tenantAdminNavItems: NavItem[] = [
  {
    title: 'Organization',
    href: '/panel/organization',
    icon: Network,
  },
  {
    title: 'Users',
    href: '/panel/users',
    icon: Users,
  },
];

// Integrations navigation - for tenant admins
const integrationsNavItems: NavItem[] = [
  {
    title: 'Xero',
    href: '/panel/integrations/xero',
    icon: Link2,
  },
];

// Intake Automation sub-navigation
const intakeNavItems: NavItem[] = [
  {
    title: 'Dashboard',
    href: '/panel/intake',
    icon: LayoutDashboard,
  },
  {
    title: 'Manage Clients',
    href: '/panel/intake/clients',
    icon: UserPlus,
  },
  {
    title: 'Document Types',
    href: '/panel/intake/document-types',
    icon: FileText,
  },
  {
    title: 'Manage FAQs',
    href: '/panel/intake/faqs',
    icon: HelpCircle,
  },
];

// Research sub-navigation
const researchNavItems: NavItem[] = [
  {
    title: 'Dashboard',
    href: '/panel/research',
    icon: LayoutDashboard,
  },
  {
    title: 'Resources',
    href: '/panel/research/resources',
    icon: Database,
  },
  {
    title: 'Researchers',
    href: '/panel/research/researchers',
    icon: Users,
  },
  {
    title: 'Institutions',
    href: '/panel/research/institutions',
    icon: Building2,
  },
  {
    title: 'Network',
    href: '/panel/research/network',
    icon: Share2,
  },
  {
    title: 'PIDs',
    href: '/panel/research/pids',
    icon: Link2,
  },
  {
    title: 'Analytics',
    href: '/panel/research/analytics',
    icon: BarChart3,
  },
  {
    title: 'Uploads',
    href: '/panel/research/uploads',
    icon: Upload,
  },
  {
    title: 'Vocabularies',
    href: '/panel/research/vocabularies',
    icon: Tag,
  },
  {
    title: 'Workspaces',
    href: '/panel/research/workspaces',
    icon: FolderOpen,
  },
  {
    title: 'Search History',
    href: '/panel/research/search-history',
    icon: Clock,
  },
  {
    title: 'Image Search',
    href: '/panel/research/image-search',
    icon: Image,
  },
  {
    title: 'Citations',
    href: '/panel/research/citations',
    icon: BookOpen,
  },
  {
    title: 'Quality Assurance',
    href: '/panel/research/quality-assurance',
    icon: Shield,
  },
  {
    title: 'Settings',
    href: '/panel/research/settings',
    icon: Settings,
  },
];

// Client Intelligence - for tenant users
// minPlan: 0=trial, 1=starter, 2=professional, 3=enterprise, 4=scale
const fileManagementNavItems: NavItem[] = [
  {
    title: 'Overview',
    href: '/panel/firm/dashboard',
    icon: BarChart3,
    minPlan: 2,
  },
  {
    title: 'Clients',
    href: '/panel/files/clients',
    icon: Users,
    minPlan: 0,
  },
  {
    title: 'Import Documents',
    href: '/panel/import',
    icon: Upload,
    minPlan: 2,
  },
  {
    title: 'Deadlines',
    href: '/panel/deadlines',
    icon: CalendarDays,
    minPlan: 2,
  },
  {
    title: 'Compliance',
    href: '/panel/compliance/checker',
    icon: Shield,
    minPlan: 2,
  },
  {
    title: 'Audit Risk',
    href: '/panel/compliance/audit-risk',
    icon: ShieldAlert,
    minPlan: 3,
  },
  {
    title: 'What-If',
    href: '/panel/compliance/scenarios',
    icon: Calculator,
    minPlan: 3,
  },
  {
    title: 'Tax Opportunities',
    href: '/panel/compliance/opportunities',
    icon: Zap,
    minPlan: 3,
  },
  {
    title: 'Tax Planning',
    href: '/panel/compliance/tax-planning',
    icon: CalendarDays,
    minPlan: 3,
  },
  {
    title: 'Reports',
    href: '/panel/reports',
    icon: FileText,
    minPlan: 2,
  },
  {
    title: 'BAS Validator',
    href: '/panel/compliance/bas',
    icon: FileCheck,
    minPlan: 2,
  },
  {
    title: 'Div 7A Checker',
    href: '/panel/compliance/div7a',
    icon: Building2,
    minPlan: 2,
  },
  {
    title: 'Missing Docs',
    href: '/panel/compliance/missing-documents',
    icon: FileSearch,
    minPlan: 2,
  },
  {
    title: 'Super Compliance',
    href: '/panel/compliance/super',
    icon: Shield,
    minPlan: 3,
  },
  {
    title: 'FBT Detector',
    href: '/panel/compliance/fbt',
    icon: AlertOctagon,
    minPlan: 3,
  },
  {
    title: 'Cash Flow',
    href: '/panel/compliance/cashflow',
    icon: BarChart3,
    minPlan: 3,
  },
  {
    title: 'Trust Distribution',
    href: '/panel/compliance/trust-distribution',
    icon: Scale,
    minPlan: 3,
  },
  {
    title: 'CGT Calculator',
    href: '/panel/compliance/cgt',
    icon: Calculator,
    minPlan: 3,
  },
  {
    title: 'Workflows',
    href: '/panel/compliance/workflows',
    icon: CheckCheck,
    minPlan: 3,
  },
  {
    title: 'Reminders',
    href: '/panel/compliance/reminders',
    icon: AlertTriangle,
    minPlan: 2,
  },
  {
    title: 'Comparison',
    href: '/panel/intelligence/comparison',
    icon: GitCompare,
    minPlan: 2,
  },
  {
    title: 'Risk Warning',
    href: '/panel/intelligence/failure-warning',
    icon: AlertOctagon,
    minPlan: 3,
  },
  {
    title: 'Reconciliation',
    href: '/panel/intelligence/reconciliation',
    icon: Scale,
    minPlan: 2,
  },
  {
    title: 'Ask All Clients',
    href: '/panel/multi-client',
    icon: MessageSquare,
    minPlan: 3,
  },
];

// AI Insights - AI-powered features
const insightsNavItems: NavItem[] = [
  {
    title: 'Action Lists',
    href: '/panel/insights/actions',
    icon: CheckCheck,
  },
  {
    title: 'Revenue Opportunities',
    href: '/panel/insights/opportunities',
    icon: DollarSign,
  },
  {
    title: 'Risk Heatmap',
    href: '/panel/insights/risk-heatmap',
    icon: AlertTriangle,
  },
  {
    title: 'Client Timeline',
    href: '/panel/insights/timeline',
    icon: Activity,
  },
  {
    title: 'Regulatory Impact',
    href: '/panel/insights/regulatory-impact',
    icon: FileWarning,
  },
  {
    title: 'Document Consistency',
    href: '/panel/insights/document-consistency',
    icon: FileCheck,
  },
];

// Content management - for tenant users
const contentNavItems: NavItem[] = [
  {
    title: 'Local Knowledge Base',
    href: '/panel/knowledge-base',
    icon: BookOpen,
  },
  {
    title: 'Global Knowledge',
    href: '/panel/global-knowledge',
    icon: Library,
  },
  {
    title: 'ATO Knowledge Base',
    href: '/panel/ato-knowledge',
    icon: BookOpen,
  },
  {
    title: 'ASIC Knowledge Base',
    href: '/panel/asic-knowledge',
    icon: Shield,
  },
  {
    title: 'URL Sources',
    href: '/panel/url-sources',
    icon: Globe,
  },
  {
    title: 'Firm AI Chat',
    href: '/panel/kb-chat',
    icon: MessageSquare,
  },
];


function NavMain({ items, label }: { items: NavItem[]; label?: string }) {
  const location = useLocation();
  const { hasPlanTier } = useAuthStore();

  return (
    <SidebarGroup className="px-2 py-0">
      {label && (
        <SidebarGroupLabel className="text-xs text-muted-foreground uppercase tracking-wider">
          {label}
        </SidebarGroupLabel>
      )}
      <SidebarMenu>
        {items.map((item) => {
          const hasAccess = item.minPlan === undefined || hasPlanTier(item.minPlan);

          if (!hasAccess) return null;

          return (
            <SidebarMenuItem key={item.title}>
              <SidebarMenuButton
                asChild
                isActive={
                  item.href === '/panel'
                    ? location.pathname === '/panel' || location.pathname === '/panel/'
                    : location.pathname === item.href ||
                      location.pathname.startsWith(item.href + '/')
                }
                tooltip={{ children: item.title }}
              >
                <Link to={item.href}>
                  <item.icon className="h-4 w-4" />
                  <span>{item.title}</span>
                </Link>
              </SidebarMenuButton>
            </SidebarMenuItem>
          );
        })}
      </SidebarMenu>
    </SidebarGroup>
  );
}

function NavIntake() {
  const location = useLocation();
  const isIntakeActive = location.pathname.startsWith('/panel/intake');

  return (
    <SidebarGroup className="px-2 py-0">
      <SidebarGroupLabel className="text-xs text-muted-foreground uppercase tracking-wider">
        Intake
      </SidebarGroupLabel>
      <SidebarMenu>
        <Collapsible
          asChild
          defaultOpen={isIntakeActive}
          className="group/collapsible"
        >
          <SidebarMenuItem>
            <CollapsibleTrigger asChild>
              <SidebarMenuButton
                tooltip={{ children: 'Intake Automation' }}
                isActive={isIntakeActive}
              >
                <ClipboardList className="h-4 w-4" />
                <span>Intake Automation</span>
                <ChevronRight className="ml-auto h-4 w-4 transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
              </SidebarMenuButton>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <SidebarMenuSub>
                {intakeNavItems.map((item) => (
                  <SidebarMenuSubItem key={item.title}>
                    <SidebarMenuSubButton
                      asChild
                      isActive={
                        item.href === '/panel/intake'
                          ? location.pathname === '/panel/intake' || location.pathname === '/panel/intake/'
                          : location.pathname === item.href ||
                            location.pathname.startsWith(item.href + '/')
                      }
                    >
                      <Link to={item.href}>
                        <item.icon className="h-4 w-4" />
                        <span>{item.title}</span>
                      </Link>
                    </SidebarMenuSubButton>
                  </SidebarMenuSubItem>
                ))}
              </SidebarMenuSub>
            </CollapsibleContent>
          </SidebarMenuItem>
        </Collapsible>
      </SidebarMenu>
    </SidebarGroup>
  );
}

function NavResearch() {
  const location = useLocation();
  const isResearchActive = location.pathname.startsWith('/panel/research');

  return (
    <SidebarGroup className="px-2 py-0">
      <SidebarGroupLabel className="text-xs text-muted-foreground uppercase tracking-wider">
        Research
      </SidebarGroupLabel>
      <SidebarMenu>
        <Collapsible
          asChild
          defaultOpen={isResearchActive}
          className="group/collapsible"
        >
          <SidebarMenuItem>
            <CollapsibleTrigger asChild>
              <SidebarMenuButton
                tooltip={{ children: 'Research Intelligence' }}
                isActive={isResearchActive}
              >
                <Microscope className="h-4 w-4" />
                <span>Research Intelligence</span>
                <ChevronRight className="ml-auto h-4 w-4 transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
              </SidebarMenuButton>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <SidebarMenuSub>
                {researchNavItems.map((item) => (
                  <SidebarMenuSubItem key={item.title}>
                    <SidebarMenuSubButton
                      asChild
                      isActive={
                        item.href === '/panel/research'
                          ? location.pathname === '/panel/research' || location.pathname === '/panel/research/'
                          : location.pathname === item.href ||
                            location.pathname.startsWith(item.href + '/')
                      }
                    >
                      <Link to={item.href}>
                        <item.icon className="h-4 w-4" />
                        <span>{item.title}</span>
                      </Link>
                    </SidebarMenuSubButton>
                  </SidebarMenuSubItem>
                ))}
              </SidebarMenuSub>
            </CollapsibleContent>
          </SidebarMenuItem>
        </Collapsible>
      </SidebarMenu>
    </SidebarGroup>
  );
}

function NavUser() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user, logout, isSuperAdmin } = useAuthStore();

  const getInitials = (email: string) => {
    return email.substring(0, 2).toUpperCase();
  };

  const handleLogout = () => {
    cognitoAuth.signOut();
    queryClient.clear(); // Clear all React Query cache to prevent stale data on re-login
    logout();
    navigate('/');
  };

  const getRoleBadge = () => {
    if (isSuperAdmin()) {
      return (
        <Badge variant="destructive" className="text-[10px] px-1 py-0">
          Super Admin
        </Badge>
      );
    }
    if (user?.role === 'tenant_admin') {
      return (
        <Badge variant="secondary" className="text-[10px] px-1 py-0">
          Admin
        </Badge>
      );
    }
    return null;
  };

  return (
    <SidebarMenu>
      {/* User Info with Dropdown */}
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger className="w-full outline-none">
            <div className="flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-sidebar-accent cursor-pointer">
              <Avatar className="h-8 w-8 rounded-lg">
                <AvatarFallback className="rounded-lg bg-primary/10 text-primary text-xs">
                  {user?.email ? getInitials(user.email) : 'AD'}
                </AvatarFallback>
              </Avatar>
              <div className="grid flex-1 text-left text-sm leading-tight">
                <div className="flex items-center gap-2">
                  <span className="truncate font-semibold">
                    {user?.email?.split('@')[0] || 'Admin'}
                  </span>
                  {getRoleBadge()}
                </div>
                <span className="truncate text-xs text-muted-foreground">
                  {user?.email || 'admin@example.com'}
                </span>
              </div>
              <ChevronsUpDown className="ml-auto h-4 w-4 text-muted-foreground" />
            </div>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" side="top" className="w-56 mb-1">
            <DropdownMenuItem onClick={() => navigate('/panel/organization')}>
              <Network className="h-4 w-4 mr-2" />
              Organization
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => navigate('/panel/users')}>
              <Users className="h-4 w-4 mr-2" />
              Users
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => navigate('/panel/integrations/xero')}>
              <Link2 className="h-4 w-4 mr-2" />
              Integrations
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => navigate('/panel/billing')}>
              <CreditCard className="h-4 w-4 mr-2" />
              Billing & Plan
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => navigate('/panel/settings')}>
              <Settings className="h-4 w-4 mr-2" />
              Settings
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleLogout} className="text-destructive focus:text-destructive">
              <LogOut className="h-4 w-4 mr-2" />
              Log out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}

export function AppSidebar() {
  const { isSuperAdmin, hasModule, hasPlanTier } = useAuthStore();
  const superAdmin = isSuperAdmin();

  // Load favourites from localStorage for sidebar shortcuts
  const [favourites, setFavourites] = useState<string[]>([]);
  useEffect(() => {
    const saved = localStorage.getItem('roni-favourites');
    if (saved) setFavourites(JSON.parse(saved));
    // Listen for changes from marketplace page
    const handler = () => {
      const updated = localStorage.getItem('roni-favourites');
      setFavourites(updated ? JSON.parse(updated) : []);
    };
    window.addEventListener('storage', handler);
    window.addEventListener('favourites-updated', handler);
    return () => { window.removeEventListener('storage', handler); window.removeEventListener('favourites-updated', handler); };
  }, []);

  return (
    <Sidebar collapsible="icon" variant="inset">
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton size="lg" asChild>
              <Link to="/panel">
                <img src={logoImg} alt="RoniDoc AI" className="h-8 w-auto" />
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>

      <SidebarContent>
        {superAdmin ? (
          <>
            {/* Super Admin sees Platform Management */}
            <NavMain items={superAdminNavItems} label="Platform" />
          </>
        ) : (
          <>
            {/* Organization/Users/Settings moved to user dropdown menu */}
            {hasModule('accounting_docs') && (
              <>
                <NavMain items={fileManagementNavItems} label="Client Intelligence" />
                <SidebarSeparator />
              </>
            )}
            {/* Integrations moved to user dropdown menu */}
            {hasModule('intake') && (
              <>
                <NavIntake />
                <SidebarSeparator />
              </>
            )}
            {hasModule('research') && (
              <>
                <NavResearch />
                <SidebarSeparator />
              </>
            )}
            <NavMain
              items={[
                { title: 'Feature Marketplace', href: '/panel/features', icon: Sparkles },
                { title: 'Help Center', href: '/panel/help', icon: HelpCircle },
              ]}
            />
            {/* Favourites from marketplace */}
            {(() => {
              const favItems = favourites
                .map(slug => FEATURES.find(f => f.slug === slug && f.route && hasPlanTier(f.minPlan)))
                .filter(Boolean)
                .map(f => ({ title: f!.name, href: f!.route!, icon: Star }));
              return favItems.length > 0 ? (
                <>
                  <SidebarSeparator />
                  <NavMain items={favItems} label="Favourites" />
                </>
              ) : null;
            })()}
            <SidebarSeparator />
            <NavMain
              items={contentNavItems.filter(item => {
                if (item.href === '/panel/global-knowledge') return hasModule('global_knowledge');
                if (item.href === '/panel/ato-knowledge') return hasModule('accounting_docs');
                if (item.href === '/panel/url-sources') return false; // Super admin only
                return true;
              })}
              label="Content"
            />
          </>
        )}
      </SidebarContent>

      <SidebarFooter>
        <NavUser />
      </SidebarFooter>
    </Sidebar>
  );
}
